import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Save, Loader2, FileText, Calendar, Bell, Mail, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientFeatures = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Document Expiry Settings
    document_expiry: {
      enabled: true,
      reminder_months: 6,
      email_subject: '📄 Your {document_name} is Expiring Soon - Action Required',
      email_heading: 'Document Expiring Soon',
      email_body: 'This is a friendly reminder that your {document_name} stored in your Life Vault will expire in approximately {months} months.',
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/client-features`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          ...data
        }));
      }
    } catch (error) {
      console.error('Error fetching client features:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/client-features`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Client features saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const updateDocumentExpiry = (field, value) => {
    setSettings(prev => ({
      ...prev,
      document_expiry: {
        ...prev.document_expiry,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-features-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white">Client Features</h1>
          <p className="text-slate-400 mt-1">Configure features available to clients in their portal</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
          data-testid="save-features-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Document Expiry Settings */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-white">Document Expiry Tracking</CardTitle>
              <CardDescription className="text-slate-400">
                Life Vault &gt; Others - Allow clients to set expiry dates on documents
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-white font-medium">Enable Expiry Date Field</p>
                <p className="text-sm text-slate-400">Show expiry date picker in Life Vault &gt; Others</p>
              </div>
            </div>
            <Switch
              checked={settings.document_expiry.enabled}
              onCheckedChange={(checked) => updateDocumentExpiry('enabled', checked)}
              data-testid="expiry-enabled-toggle"
            />
          </div>

          {settings.document_expiry.enabled && (
            <>
              {/* Reminder Period */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-amber-400" />
                    Reminder Period
                  </Label>
                  <Select
                    value={String(settings.document_expiry.reminder_months)}
                    onValueChange={(value) => updateDocumentExpiry('reminder_months', parseInt(value))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="reminder-months-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="1" className="text-white hover:bg-slate-700">1 month before</SelectItem>
                      <SelectItem value="2" className="text-white hover:bg-slate-700">2 months before</SelectItem>
                      <SelectItem value="3" className="text-white hover:bg-slate-700">3 months before</SelectItem>
                      <SelectItem value="6" className="text-white hover:bg-slate-700">6 months before</SelectItem>
                      <SelectItem value="9" className="text-white hover:bg-slate-700">9 months before</SelectItem>
                      <SelectItem value="12" className="text-white hover:bg-slate-700">12 months before</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">When to send reminder email before document expires</p>
                </div>
              </div>

              {/* Email Customization */}
              <div className="border-t border-slate-700 pt-6 space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">Email Notification Settings</h3>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Email Subject</Label>
                  <Input
                    value={settings.document_expiry.email_subject}
                    onChange={(e) => updateDocumentExpiry('email_subject', e.target.value)}
                    placeholder="📄 Your {document_name} is Expiring Soon"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="email-subject-input"
                  />
                  <p className="text-xs text-slate-500">Use {'{document_name}'} as placeholder for the document name</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Email Heading</Label>
                  <Input
                    value={settings.document_expiry.email_heading}
                    onChange={(e) => updateDocumentExpiry('email_heading', e.target.value)}
                    placeholder="Document Expiring Soon"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="email-heading-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Email Body Message</Label>
                  <Textarea
                    value={settings.document_expiry.email_body}
                    onChange={(e) => updateDocumentExpiry('email_body', e.target.value)}
                    placeholder="This is a friendly reminder that your {document_name} stored in your Life Vault will expire..."
                    className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
                    data-testid="email-body-input"
                  />
                  <p className="text-xs text-slate-500">
                    Available placeholders: {'{document_name}'}, {'{months}'}, {'{expiry_date}'}, {'{client_name}'}
                  </p>
                </div>
              </div>

              {/* Preview Card */}
              <div className="border-t border-slate-700 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Email Preview</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchSettings}
                    className="text-slate-400 border-slate-700 hover:bg-slate-800"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset to Default
                  </Button>
                </div>
                <div className="bg-slate-950 rounded-lg border border-slate-700 p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <span className="font-medium">Subject:</span>
                      <span className="text-white">
                        {settings.document_expiry.email_subject.replace('{document_name}', 'Passport')}
                      </span>
                    </div>
                    <div className="border-t border-slate-800 pt-4">
                      <h4 className="text-xl font-bold text-blue-400 mb-2">
                        {settings.document_expiry.email_heading}
                      </h4>
                      <p className="text-slate-300">
                        {settings.document_expiry.email_body
                          .replace('{document_name}', 'Passport')
                          .replace('{months}', String(settings.document_expiry.reminder_months))
                          .replace('{expiry_date}', '15 Jul 2026')
                          .replace('{client_name}', 'John Doe')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-slate-800/30 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h4 className="text-white font-medium mb-1">How Document Expiry Works</h4>
              <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
                <li>Clients can optionally set an expiry date when adding documents to Life Vault &gt; Others</li>
                <li>A scheduled job runs daily at 9:30 AM IST to check for expiring documents</li>
                <li>Clients receive a reminder email based on the configured reminder period above</li>
                <li>Each document only triggers one reminder notification</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientFeatures;
